import React, { Fragment, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SubmissionError } from "redux-form"; // ES6
import Sticky from "react-stickynode";
import Recipe from "./Recipe/Recipe";
import "./CreateRecipe.scss";
import { get, post, put, patch } from "@common/services/request";
import ConfirmationModal from "../../../common/components/ConfirmationModal/ConfirmationModal";
import { calculateBeerProfile } from "./utils";
import TitleBar from "../../../common/components/TitleBar/TitleBar";
import BeerProfileBar from "../../../common/components/BeerProfileBar/BeerProfileBar";
import {
  NewIngredientProvider,
  NewIngredientConsumer,
} from "./NewIngredientContext";
import * as ingredientActions from "../../../beers/actions/ingredientActions";
import CustomIngredientModal from "../CustomIngredientList/CustomIngredientModal";
import { validateRecipe as validateRecipeService } from "@beers/services/recipeService";
import { apiEndpoints as api, routes } from "@common/constants/urls";
import { removeNullIngredients } from "../../../util/recipeHelper";
import { logError } from "../../../common/util/exceptionHandler";
import BeerStyleComparison from "./BeerStyleComparison/BeerStyleComparison";

const CreateRecipe = (props) => {
  const dispatch = useDispatch();
  const form = useSelector((state) => state.form);
  const beerProfile = useSelector((state) =>
    calculateBeerProfile(state.form.recipe),
  );

  const urlParams = new URLSearchParams(window.location.search);

  const [beerName, setBeerName] = useState("");
  const [beerStyle, setBeerStyle] = useState("");
  const [beerStyleID, setBeerStyleID] = useState(null);
  const [beerCarbonation, setBeerCarbonation] = useState(null);
  const [mode, setMode] = useState(urlParams.get("mode") || null);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [shouldValidateInitial, setShouldValidateInitial] = useState(
    urlParams.get("mode") === "validate",
  );
  const [isSaving, setIsSaving] = useState(false);

  const fetchIngredients = () => {
    dispatch(ingredientActions.fetchHops());
    dispatch(ingredientActions.fetchFermentables());
    dispatch(ingredientActions.fetchYeasts());
    dispatch(ingredientActions.fetchAdjuncts());
    dispatch(ingredientActions.fetchLaboratories());
  };

  useEffect(() => {
    fetchIngredients();

    const beer_id = props.match.params.id;
    const currentMode = props.location.search.split("=")[1];
    setMode(currentMode);

    const fetchBeer = async () => {
      try {
        const beer = await get(`${api.beers}${beer_id}/`);
        setBeerName(beer.name);
        setBeerStyle(beer.style.name);
        setBeerStyleID(beer.style.id);
        setBeerCarbonation(beer.carbonation);
      } catch (e) {
        logError(e, null, { action: "Failed to fetch beer details" });
      }
    };

    fetchBeer();
  }, []);

  const saveRecipe = async (recipe, recipeId, saveMethod) => {
    try {
      if (
        recipe.carbonation !== undefined &&
        recipe.carbonation !== null &&
        recipe.carbonation !== ""
      ) {
        const carbFormatted = parseFloat(recipe.carbonation).toFixed(2);
        recipe.carbonation = carbFormatted;
        const beerPayload = {
          carbonation: carbFormatted,
        };
        if (beerName) beerPayload.name = beerName;
        if (beerStyleID) beerPayload.style = beerStyleID;

        try {
          await patch(`${api.beers}${recipe.beer_id}/`, beerPayload);
        } catch (patchErr) {
          try {
            await put(`${api.beers}${recipe.beer_id}/`, beerPayload);
          } catch (putErr) {
            logError(putErr, null, {
              action: "Failed to update beer carbonation",
            });
          }
        }
      }
      await saveMethod(`${api.recipes}${recipeId}`, recipe);
      setIsSaving(false);
      props.history.push(`${routes.beers}${recipe.beer_id}`);
    } catch (e) {
      const extraInfo = { recipeId, recipe };
      logError(e, null, extraInfo);
      setIsSaving(false);
    }
  };

  const validateRecipe = async (recipe) => {
    const urlRecipeId = props.match.params.recipeid;
    const newRecipe = urlRecipeId === "new";
    const newVersion = props.match.path.includes("newversion");
    const recipeId = newRecipe || newVersion ? "" : `${urlRecipeId}/`;

    setIsSaving(true);

    const saveMethod = newRecipe || newVersion ? post : put;

    // Normalize beer profile fields
    recipe.fg = recipe.fg ? parseFloat(recipe.fg).toFixed(3) : 0;
    recipe.og = recipe.og ? parseFloat(recipe.og).toFixed(3) : 0;
    recipe.ibu = recipe.ibu ? parseInt(recipe.ibu) : 0;
    if (
      recipe.carbonation !== undefined &&
      recipe.carbonation !== null &&
      recipe.carbonation !== ""
    ) {
      recipe.carbonation = parseFloat(recipe.carbonation).toFixed(2);
    } else {
      delete recipe.carbonation;
    }

    try {
      await validateRecipeService(recipe);
      await saveRecipe(recipe, recipeId, saveMethod);
    } catch (err) {
      setIsConfirmationModalVisible(true);
      setModalAction(() => () => saveRecipe(recipe, recipeId, saveMethod));
      setIsSaving(false);
      throw new SubmissionError(err.errors || err);
    }
  };

  const handleSubmit = async (formData) => {
    setModalAction(null);
    const recipe = {
      ...removeNullIngredients(formData),
      beer_id: props.match.params.id,
    };

    return await validateRecipe(recipe);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalVisible(false);
    setShouldValidateInitial(true);
  };

  const scrollToError = () => {
    const { submitErrors } = form.recipe;
    const errorName = Object.keys(submitErrors)[0];

    let errorElement = null;
    errorElement = document.querySelector(`[name="${errorName}"]`);
    if (!errorElement) {
      errorElement = document.querySelector(`[data-name="${errorName}"]`);
    }

    if (errorElement) {
      const position =
        errorElement.getBoundingClientRect().top +
        document.documentElement.scrollTop;
      window.scrollTo({ top: position - 200, behavior: "smooth" });
    }
  };

  const recipeId = props.match.params.recipeid;
  const modalMessage = (
    <Fragment>
      <p style={{ fontFamily: "Gotham-Book" }}>
        The current recipe is not brewable, would you like to save anyway? You
        can review the highlighted fields and alert messages for further
        information.
      </p>
    </Fragment>
  );

  return (
    <div>
      <NewIngredientProvider>
        <TitleBar title={beerName.toUpperCase()} subtitle={beerStyle} />
        <Sticky enabled={true} top={80} innerZ={100}>
          <BeerProfileBar
            beerProfile={{
              ...(form.recipe && form.recipe.values),
              carbonation:
                (form.recipe &&
                  form.recipe.values &&
                  form.recipe.values.carbonation) ||
                beerCarbonation,
            }}
          />
        </Sticky>
        <NewIngredientConsumer>
          {({ show, hideModal, setActiveKey, activeTabKey, onSuccess }) => (
            <CustomIngredientModal
              show={show}
              onHide={hideModal}
              activeTabKey={activeTabKey}
              setActiveKey={setActiveKey}
              ingredient={null}
              getAllIngredients={fetchIngredients}
              showOnlyActiveKey
              onSuccess={onSuccess}
            />
          )}
        </NewIngredientConsumer>
        <div className="CreateRecipe">
          <div className="container">
            <div className="row">
              <div className="hidden-md hidden-sm hidden-xs col-lg-2">
                <Sticky enabled={true} top={140} innerZ={100}>
                  <BeerStyleComparison
                    styleID={beerStyleID}
                    beerProfile={
                      beerProfile && [
                        {
                          ...beerProfile[0],
                          carbonation:
                            (form.recipe &&
                              form.recipe.values &&
                              form.recipe.values.carbonation) ||
                            beerCarbonation,
                        },
                        beerProfile[1],
                      ]
                    }
                  />
                </Sticky>
              </div>
              <div className="hidden-lg col-xs-12 col-sm-12 col-md-12">
                <BeerStyleComparison
                  styleID={beerStyleID}
                  beerProfile={
                    beerProfile && [
                      {
                        ...beerProfile[0],
                        carbonation:
                          (form.recipe &&
                            form.recipe.values &&
                            form.recipe.values.carbonation) ||
                          beerCarbonation,
                      },
                      beerProfile[1],
                    ]
                  }
                />
              </div>
              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-10">
                <Recipe
                  onSubmit={handleSubmit}
                  recipeId={recipeId}
                  beerProfile={beerProfile}
                  beerCarbonation={beerCarbonation}
                  validationEnabled={mode === "validate"}
                  shouldValidateInitial={shouldValidateInitial}
                  isSaving={isSaving}
                />
              </div>
            </div>
          </div>
        </div>
      </NewIngredientProvider>
      <ConfirmationModal
        show={isConfirmationModalVisible}
        hide={closeConfirmationModal}
        title="BEFORE SAVING..."
        msg={modalMessage}
        action={modalAction}
        cancelLabel="Edit recipe"
        confirmLabel="Save"
        confirmStyle="primary"
        onCancel={scrollToError}
      />
    </div>
  );
};

export default CreateRecipe;
